import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import homeFill from '@iconify/icons-eva/home-fill';
import personFill from '@iconify/icons-eva/person-fill';
import settings2Fill from '@iconify/icons-eva/settings-2-fill';
import lockFill from '@iconify/icons-eva/lock-fill';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// material
import { alpha } from '@material-ui/core/styles';
import { Button, Box, Divider, MenuItem, Typography, Switch } from '@material-ui/core';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// hooks
import useAuth from '../../hooks/useAuth';
import useIsMountedRef from '../../hooks/useIsMountedRef';
// components
import { MIconButton } from '../../components/@material-extend';
import MyAvatar from '../../components/MyAvatar';
import MenuPopover from '../../components/MenuPopover';
import { VERSION } from '../../constants/defaultValues';
import { isSuperAdminOrAdmin } from './DashboardNavbar';

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  {
    label: 'Home',
    icon: homeFill,
    linkTo: PATH_DASHBOARD.root
  },
  {
    label: 'Profile',
    icon: personFill,
    linkTo: PATH_DASHBOARD.user.account
  },
  {
    label: 'Password',
    icon: lockFill,
    linkTo: `${PATH_DASHBOARD.user.account}#change_password`
  }
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const anchorRef = useRef(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();
  const { user, logout, selectedFacility, setSelectedFacility } = useAuth();
  const [open, setOpen] = useState(false);

  const isAllowedUser = isSuperAdminOrAdmin(user);
  const filteredMenuOptions = isAllowedUser 
    ? MENU_OPTIONS 
    : MENU_OPTIONS.filter((option) => option.label !== 'Home');

  const isDarkMode = selectedFacility === '14' || selectedFacility === '13';
  const showToggle = ['7', '10', '13', '14'].includes(selectedFacility);

  const handleToggleDarkMode = async (event) => {
    const checked = event.target.checked;
    let nextFacilityId = null;

    if (checked) {
      if (selectedFacility === '10') {
        nextFacilityId = '14';
      } else if (selectedFacility === '7') {
        nextFacilityId = '13';
      }
    } else {
      if (selectedFacility === '14') {
        nextFacilityId = '10';
      } else if (selectedFacility === '13') {
        nextFacilityId = '7';
      }
    }

    if (nextFacilityId) {
      window.localStorage.setItem('selectedFacility', nextFacilityId);
      window.location.href = '/dashboard/maindashboard';
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout', { variant: 'error' });
    }
  };

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72)
            }
          })
        }}
      >
        <MyAvatar />
      </MIconButton>

      <MenuPopover open={open} onClose={handleClose} anchorEl={anchorRef.current} sx={{ width: 220 }}>
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle1" noWrap>
            {`${user?.firstName} ${user?.lastName}`}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user.email}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        {filteredMenuOptions.map((option) => (
          <MenuItem
            key={option.label}
            to={option.linkTo}
            component={RouterLink}
            onClick={handleClose}
            sx={{ typography: 'body2', py: 1, px: 2.5 }}
          >
            <Box
              component={Icon}
              icon={option.icon}
              sx={{
                mr: 2,
                width: 24,
                height: 24
              }}
            />

            {option.label}
          </MenuItem>
        ))}

        {showToggle && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                Dark Mode
              </Typography>
              <Switch
                checked={isDarkMode}
                onChange={handleToggleDarkMode}
                color="primary"
                size="small"
              />
            </Box>
          </>
        )}

        <Box sx={{ p: 2, pt: 1.5 }}>
          <Button fullWidth color="inherit" variant="outlined" onClick={handleLogout}>
            Logout
          </Button>
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 1.5, fontSize: '0.75rem' }}>
            V - {VERSION}
          </Typography>
        </Box>
      </MenuPopover>
    </>
  );
}
